import React from 'react';
import Typography from '@mui/material/Typography';
import {
  AppBar,
  Toolbar,
  IconButton,
  Button,
  CircularProgress,
  Container,
  Paper,
  ListItemText,
  Grid,
  Snackbar,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  Select,
  MenuItem,
  Input,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
} from '@mui/material';
import BackIcon from '@mui/icons-material/ArrowBack';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import TocIcon from '@mui/icons-material/Toc';
import BarChartIcon from '@mui/icons-material/BarChart';
import CodeIcon from '@mui/icons-material/Code';
import autobind from 'autobind-decorator';
import { RouterStore } from 'mobx-react-router';
import AccountService from '../../service/AccountService';
import { inject, observer } from 'mobx-react';
import DeleteIcon from '@mui/icons-material/Delete';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import LogoutButton from '../common/LogoutButton';
import FileService, { NarrativeMetadata } from '../../service/FileService';
import { observable } from 'mobx';
import Alert from '@mui/material/Alert';
import { styled } from '@mui/material/styles';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import exportFromJSON, { ExportType } from 'export-from-json';
import CustomHorizontalChart from './components/CustomHorizontalChart';
import { withStyles } from '@mui/styles';

const TableCellHoverStyled = withStyles({
  label: {
    color: '#000',
  },
  root: {
    color: '#000',
    '&:hover': {
      cursor: 'pointer',
    },
  },
})(TableCell);

const NavButton = withStyles({
  label: {
    color: '#FFF',
  },
})(Button);

type NarrativeListPageProps = {
  routing?: RouterStore;
  account?: AccountService;
  files?: FileService;
  match?: any;
};

type TypeRendred = 'TABLE' | 'CHART' | 'API';

type NarrativeListState = {
  customNarrativeList?: any[];
  renderType: TypeRendred;
  showApiKey: boolean;
  showPrivateKey: boolean;
};

type ChartListStatus = 'LOADING' | 'NORMAL';

type Order = 'asc' | 'desc';

@inject('account')
@inject('routing')
@inject('files')
@observer
export default class NarrativeListPage extends React.Component<
  NarrativeListPageProps,
  NarrativeListState
> {
  @observable
  private _status: ChartListStatus = 'LOADING';

  @observable
  private _deletingNarrative: string = null;

  @observable
  private _filesOrderBy = 'date';

  @observable
  private _filesOrder: Order = 'desc';

  @observable
  private narratives = this.props.files?.narratives
    .filter((n) => n.clinic == this.props.account.currentClinic)
    .map((item) =>
      Object.assign(item, {
        flowName: this.props.files?.files.find((i) => i.fileId == item.flowId)
          ? this.props.files?.files.find((i) => i.fileId == item.flowId).name
          : item.flowName,
      })
    );

  @observable
  private currentNarratives = this.narratives.sort((itemA, itemB) =>
    new Date(itemA.date) > new Date(itemB.date)
      ? -1
      : new Date(itemA.date) < new Date(itemB.date)
      ? 1
      : 0
  );

  @observable
  private currentWorkflows = this.currentNarratives
    ?.map((item) => Object.assign({}, { id: item.flowId, name: item.flowName }))
    .filter(
      (item, index, self) =>
        index == self.findIndex((t) => t.id == item.id && t.name == item.name)
    );

  // @observable
  // private totalQuestionsList = this.chartData(this.currentNarratives);

  @observable
  private filterValue = [];

  @observable
  private _deleteDialog: boolean = false;

  @observable
  private _narrativeToDelete: NarrativeMetadata = null;

  @observable
  snackMessage = '';

  state: NarrativeListState = {
    customNarrativeList: [],
    renderType: 'TABLE',
    showApiKey: false,
    showPrivateKey: false,
  };

  async copyToClipboard(str: string, type: string) {
    await navigator.clipboard.writeText(str);
    this.snackMessage = `${type} copied to clipboard`;
  }

  handleRequestSort(event: React.MouseEvent<unknown>, property: string) {
    const isAsc = this._filesOrderBy === property && this._filesOrder === 'asc';
    this._filesOrder = isAsc ? 'desc' : 'asc';
    this._filesOrderBy = property;

    if (this._filesOrderBy === 'workflow') {
      if (this._filesOrder === 'asc') {
        this.currentNarratives = this.currentNarratives.sort((itemA, itemB) =>
          itemA.flowName > itemB.flowName
            ? 1
            : itemA.flowName < itemB.flowName
            ? -1
            : 0
        );
      } else if (this._filesOrder === 'desc') {
        this.currentNarratives = this.currentNarratives.sort((itemA, itemB) =>
          itemA.flowName > itemB.flowName
            ? -1
            : itemA.flowName < itemB.flowName
            ? 1
            : 0
        );
      }
    }
    if (this._filesOrderBy === 'name') {
      if (this._filesOrder === 'asc') {
        this.currentNarratives = this.currentNarratives.sort((itemA, itemB) =>
          itemA.userName > itemB.userName
            ? 1
            : itemA.userName < itemB.userName
            ? -1
            : 0
        );
      } else if (this._filesOrder === 'desc') {
        this.currentNarratives = this.currentNarratives.sort((itemA, itemB) =>
          itemA.userName > itemB.userName
            ? -1
            : itemA.userName < itemB.userName
            ? 1
            : 0
        );
      }
    }
    if (this._filesOrderBy === 'date') {
      if (this._filesOrder === 'asc') {
        this.currentNarratives = this.currentNarratives.sort((itemA, itemB) =>
          new Date(itemA.date) > new Date(itemB.date)
            ? 1
            : new Date(itemA.date) < new Date(itemB.date)
            ? -1
            : 0
        );
      } else if (this._filesOrder === 'desc') {
        this.currentNarratives = this.currentNarratives.sort((itemA, itemB) =>
          new Date(itemA.date) > new Date(itemB.date)
            ? -1
            : new Date(itemA.date) < new Date(itemB.date)
            ? 1
            : 0
        );
      }
    }
  }

  @autobind
  goBack() {
    this.props.account.goBack();
  }

  @autobind
  gotoPlan() {
    var { routing } = this.props;
    routing.push(`/plan`);
  }

  @autobind
  handleUpgradeClick() {
    var { routing } = this.props;
    routing.push(`/plan`);
  }

  gotoNarrative(fileId: string) {
    var { routing } = this.props;
    routing.push(`/narrative/${fileId}`);
  }

  async componentDidMount() {
    const { id } = this.props.match.params;
    var { files } = this.props;
    this._status = 'LOADING';
    await files.refreshNarrativeList();
    let fileList = this.props.files?.files;
    let narratives = this.props.files?.narratives.filter(
      (n) => n.clinic == this.props.account.currentClinic
    );
    narratives.forEach(
      (item) =>
        (item.flowName = fileList.find((i) => i.fileId == item.flowId)
          ? fileList.find((i) => i.fileId == item.flowId).name
          : item.flowName)
    );
    if (id) {
      this.currentNarratives = narratives
        .filter((item) => item.flowId === id)
        .sort((itemA, itemB) =>
          new Date(itemA.date) > new Date(itemB.date)
            ? -1
            : new Date(itemA.date) < new Date(itemB.date)
            ? 1
            : 0
        );
      this.currentWorkflows = narratives
        .map((item) =>
          Object.assign({}, { id: item.flowId, name: item.flowName })
        )
        .filter(
          (item, index, self) =>
            index ==
            self.findIndex((t) => t.id == item.id && t.name == item.name)
        );

      this.filterValue = [id];
    } else {
      this.currentNarratives = narratives.sort((itemA, itemB) =>
        new Date(itemA.date) > new Date(itemB.date)
          ? -1
          : new Date(itemA.date) < new Date(itemB.date)
          ? 1
          : 0
      );
      this.currentWorkflows = narratives
        .map((item) =>
          Object.assign({}, { id: item.flowId, name: item.flowName })
        )
        .filter(
          (item, index, self) =>
            index ==
            self.findIndex((t) => t.id == item.id && t.name == item.name)
        );
    }
    this._status = 'NORMAL';
  }

  @autobind
  async handleFilter(
    // event: React.ChangeEvent<{ value: string[] }>,
    event: any,
    child: any
  ) {
    var value = child.props.value;

    if (value == 'ALL') {
      this.filterValue = ['ALL'];
    } else {
      this.filterValue = event.target.value.filter((it) => it != 'ALL');
    }

    if (this.filterValue.includes('ALL') || this.filterValue.length == 0) {
      this.currentNarratives = this.props.files?.narratives
        .filter((n) => n.clinic == this.props.account.currentClinic)
        .map((item) =>
          Object.assign(item, {
            flowName: this.props.files?.files.find(
              (i) => i.fileId == item.flowId
            )
              ? this.props.files?.files.find((i) => i.fileId == item.flowId)
                  .name
              : item.flowName,
          })
        );
    } else {
      this.currentNarratives = this.props.files?.narratives
        .map((item) =>
          Object.assign(item, {
            flowName: this.props.files?.files.find(
              (i) => i.fileId == item.flowId
            )
              ? this.props.files?.files.find((i) => i.fileId == item.flowId)
                  .name
              : item.flowName,
          })
        )
        .filter((item) => this.filterValue.includes(item.flowId));
    }
  }

  @autobind
  renderNavBar() {
    var userType = this.props.account.userData.userType;
    const { routing } = this.props;
    return (
      <AppBar position='static'>
        <Toolbar variant='dense'>
          <IconButton
            edge='start'
            color='inherit'
            aria-label='menu'
            onClick={routing.goBack}
          >
            <BackIcon />
          </IconButton>
          <Typography variant='h6' style={{ flexGrow: 1, color: 'inherit' }}>
            Responses
          </Typography>

          {userType == 'FREE_USER' && (
            <NavButton aria-label='menu' onClick={this.gotoPlan}>
              <UnarchiveIcon style={{ marginRight: '10px' }} />
              UPGRADE
            </NavButton>
          )}

          <LogoutButton />
        </Toolbar>
      </AppBar>
    );
  }

  handleDeleteNarrative(narrative: NarrativeMetadata) {
    this._narrativeToDelete = narrative;
    this.handleDeleteDialogOpen();
  }

  async deleteNarrative(id: string) {
    this._deletingNarrative = id;
    var { files } = this.props;
    await files.deleteNarrative(id);

    this._deletingNarrative = null;
  }

  @autobind
  async handleDeleteDialogClose(response?: boolean) {
    if (response) {
      await this.deleteNarrative(this._narrativeToDelete.id.toString());
      this.currentNarratives = this.props.files?.narratives.filter(
        (n) => n.clinic == this.props.account.currentClinic
      );
      // this.currentWorkflows = this.props.files?.narratives.map(item=>item.flowName).reduce((array,currentItem)=>array.includes(currentItem)?array:[...array,currentItem],[])
      this.currentWorkflows = this.props.files?.narratives
        .map((item) =>
          Object.assign({}, { id: item.flowId, name: item.flowName })
        )
        .filter(
          (item, index, self) =>
            index ==
            self.findIndex((t) => t.id == item.id && t.name == item.name)
        );
    }

    this._deleteDialog = false;
  }

  @autobind
  handleDeleteDialogOpen() {
    this._deleteDialog = true;
  }

  @autobind
  exportHandler(exportType: ExportType) {
    const { id } = this.props.match.params;
    const newNarrativeList = this.currentNarratives.map((n) => {
      let quesAnsObj: object = {};
      n.responses.forEach((item, i) => {
        quesAnsObj[`question ${i + 1}`] = item.q;
        quesAnsObj[`answer ${i + 1}`] = item.a;
      });
      return {
        id: n.id,
        name: n.userName,
        location: this.props.account.getCurrentClinicName(),
        workFlow: n.flowName,
        date: new Date(n.date).toLocaleString(),
        ...quesAnsObj,
        narrative: n.narrative,
      };
    });
    const data = newNarrativeList;
    const fileName = `responses_${id}`;
    exportFromJSON({ data, fileName, exportType });
  }

  getSelectedFlowNames(selected: any): string {
    let flowNames = this.currentWorkflows
      .filter((flow) => selected.includes(flow.id))
      .map((item) => item.name);
    if (selected.includes('ALL')) {
      return `ALL ${flowNames.join(', ')}`;
    } else {
      return flowNames.join(', ');
    }
  }

  renderFiles() {
    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const MenuProps = {
      PaperProps: {
        style: {
          maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
          width: 250,
        },
      },
    };
    return (
      <>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-evenly',
            margin: '20px 0',
            marginBottom: '30px',
            padding: '3rem 2rem 1rem',
          }}
        >
          <Button
            variant='contained'
            color='primary'
            style={{
              margin: '0 10px',
              backgroundColor: '#6C757D',
            }}
            onClick={() => this.exportHandler(exportFromJSON.types.csv)}
          >
            Export CSV
          </Button>
          <Button
            variant='contained'
            color='primary'
            style={{
              margin: '0 10px',
              backgroundColor: '#6C757D',
            }}
            onClick={() => this.exportHandler(exportFromJSON.types.json)}
          >
            Export JSON
          </Button>
          <Button
            variant='contained'
            color='primary'
            style={{
              margin: '0 10px',
              backgroundColor: '#6C757D',
            }}
            onClick={() => this.exportHandler(exportFromJSON.types.xml)}
          >
            Export XML
          </Button>
        </div>

        <TableContainer>
          <Table
            aria-labelledby='tableTitle'
            size={'medium'}
            aria-label='enhanced table'
          >
            <TableHead>
              <TableRow>
                <TableCell
                  style={{ verticalAlign: 'baseline' }}
                  key='name'
                  align='left'
                  sortDirection={
                    this._filesOrderBy === 'name' ? this._filesOrder : false
                  }
                >
                  <TableSortLabel
                    active={this._filesOrderBy === 'name'}
                    direction={
                      this._filesOrderBy === 'name' ? this._filesOrder : 'asc'
                    }
                    onClick={(e) => this.handleRequestSort(e, 'name')}
                  >
                    NAME
                  </TableSortLabel>
                </TableCell>

                <TableCell
                  style={{ verticalAlign: 'baseline' }}
                  key='location'
                  align='left'
                  sortDirection={
                    this._filesOrderBy === 'location' ? this._filesOrder : false
                  }
                >
                  <TableSortLabel
                    active={this._filesOrderBy === 'location'}
                    direction={
                      this._filesOrderBy === 'location'
                        ? this._filesOrder
                        : 'asc'
                    }
                    onClick={(e) => this.handleRequestSort(e, 'location')}
                  >
                    LOCATION
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  key='workflow'
                  align='left'
                  sortDirection={
                    this._filesOrderBy === 'workflow' ? this._filesOrder : false
                  }
                >
                  <TableSortLabel
                    active={this._filesOrderBy === 'workflow'}
                    direction={
                      this._filesOrderBy === 'workflow'
                        ? this._filesOrder
                        : 'asc'
                    }
                    onClick={(e) => this.handleRequestSort(e, 'workflow')}
                  >
                    WORKFLOW
                  </TableSortLabel>
                  <Select
                    multiple
                    value={this.filterValue}
                    onChange={this.handleFilter}
                    input={<Input />}
                    MenuProps={MenuProps}
                    renderValue={(selected) =>
                      this.getSelectedFlowNames(selected)
                    }
                    style={{
                      width: '-webkit-fill-available',
                      maxWidth: '200px',
                      marginTop: '8px',
                    }}
                  >
                    <MenuItem key='-1' value='ALL'>
                      <Checkbox
                        checked={this.filterValue.indexOf('ALL') > -1}
                      />
                      <ListItemText primary='ALL' />
                    </MenuItem>
                    {this.currentWorkflows.map((item) => (
                      <MenuItem key={`${item.id}-${item.name}`} value={item.id}>
                        <Checkbox
                          checked={this.filterValue.indexOf(item.id) > -1}
                        />
                        <ListItemText primary={item.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>

                <TableCell
                  style={{ verticalAlign: 'baseline' }}
                  key='time'
                  align='left'
                  sortDirection={
                    this._filesOrderBy === 'date' ? this._filesOrder : false
                  }
                >
                  <TableSortLabel
                    active={this._filesOrderBy === 'date'}
                    direction={
                      this._filesOrderBy === 'date' ? this._filesOrder : 'desc'
                    }
                    onClick={(e) => this.handleRequestSort(e, 'date')}
                  >
                    SUBMITTED TIME
                  </TableSortLabel>
                </TableCell>

                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {this.currentNarratives.map((narrative, index) => (
                <React.Fragment key={`file-item-${narrative.id}`}>
                  <TableRow hover>
                    <TableCellHoverStyled
                      align='left'
                      onClick={() =>
                        this.gotoNarrative(narrative.id.toString())
                      }
                    >
                      {narrative.userName}
                    </TableCellHoverStyled>

                    <TableCellHoverStyled
                      align='left'
                      onClick={() =>
                        this.gotoNarrative(narrative.id.toString())
                      }
                    >
                      {this.props.account.getCurrentClinicName()}
                    </TableCellHoverStyled>

                    <TableCellHoverStyled
                      align='left'
                      onClick={() =>
                        this.gotoNarrative(narrative.id.toString())
                      }
                    >
                      {narrative.flowName}
                    </TableCellHoverStyled>

                    <TableCellHoverStyled
                      align='left'
                      onClick={() =>
                        this.gotoNarrative(narrative.id.toString())
                      }
                    >
                      {new Date(narrative.date).toLocaleString()}
                    </TableCellHoverStyled>
                    <TableCell align='right'>
                      {this._deletingNarrative == narrative.id ? (
                        <CircularProgress size={18} />
                      ) : (
                        <IconButton
                          edge='end'
                          aria-label='delete'
                          disabled={this._deletingNarrative != null}
                          onClick={() => this.handleDeleteNarrative(narrative)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  }

  renderLoading() {
    return (
      <Grid container justifyContent='center' spacing={4} key='files-loading'>
        <Grid item>
          <CircularProgress />
        </Grid>
      </Grid>
    );
  }

  renderEmpty(str: string) {
    return (
      <Grid container justifyContent='center' key='files-empty'>
        <Grid item>
          <Typography style={{ textTransform: 'none' }}>
            {`There are no narratives to show ${str}.`}
          </Typography>
        </Grid>
      </Grid>
    );
  }

  renderTable() {
    var { narratives } = this.props.files;

    var currentNarratives = narratives.filter(
      (n) => n.clinic == this.props.account.currentClinic
    );

    if (this._status == 'LOADING') {
      return this.renderLoading();
    }

    if (currentNarratives.length == 0) {
      return this.renderEmpty('');
    }

    return this.renderFiles();
  }

  @autobind
  renderQuestionsHelp() {
    if (this._status == 'LOADING') {
      return this.renderLoading();
    }

    if (this.currentNarratives.length == 0) {
      return this.renderEmpty('');
    }
    return (
      <div style={{ padding: '1.3rem 1rem', marginBottom: '1rem' }}>
        <h2
          style={{
            fontSize: '1.2rem',
            fontWeight: '500',
            color: 'darkred',
            textDecoration: 'underline',
            marginLeft: '0.5rem',
          }}
        >
          HINT:
        </h2>
        {this.currentNarratives[0].responses.map((resp, idx) => (
          <div>
            <span style={{ fontWeight: '600' }}>- Q{idx + 1} : </span>
            {resp.q}
          </div>
        ))}
      </div>
    );
  }

  @autobind
  renderAPI() {
    const apiKey = this.props.match.params.id;
    const privateKey = this.props.account.user.uid;
    const apiUrl =
      'https://us-central1-ai-flowchart.cloudfunctions.net/api/questionnaire?API_KEY=YOUR_API_KEY&PRIVATE_KEY=YOUR_PRIVATE_KEY';
    return (
      <>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: '1rem 2rem',
          }}
        >
          <h1
            style={{
              fontSize: '22px',
              margin: '10px 0',
              lineHeight: 'inherit',
              fontWeight: 'inherit',
            }}
          >
            Responses API
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <FormControl
              variant='outlined'
              style={{ flex: '1', margin: '20px' }}
            >
              <InputLabel htmlFor='apiKey'>API KEY</InputLabel>
              <OutlinedInput
                id='apiKey'
                type={this.state.showApiKey ? 'text' : 'password'}
                value={apiKey}
                inputProps={{
                  readOnly: true,
                }}
                endAdornment={
                  <InputAdornment position='end'>
                    <IconButton
                      aria-label='toggle password visibility'
                      onClick={() =>
                        this.setState({
                          ...this.state,
                          showApiKey: !this.state.showApiKey,
                        })
                      }
                      edge='end'
                    >
                      {this.state.showApiKey ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                    <IconButton
                      aria-label='toggle password visibility'
                      onClick={() => this.copyToClipboard(apiKey, 'API Key')}
                      edge='end'
                    >
                      <FileCopyIcon />
                    </IconButton>
                  </InputAdornment>
                }
                label='API KEY'
              />
            </FormControl>
            <FormControl
              variant='outlined'
              style={{ flex: '1', margin: '20px' }}
            >
              <InputLabel htmlFor='privateKey'>PRIVATE KEY</InputLabel>
              <OutlinedInput
                id='privateKey'
                type={this.state.showPrivateKey ? 'text' : 'password'}
                value={privateKey}
                inputProps={{
                  readOnly: true,
                }}
                endAdornment={
                  <InputAdornment position='end'>
                    <IconButton
                      aria-label='toggle password visibility'
                      onClick={() =>
                        this.setState({
                          ...this.state,
                          showPrivateKey: !this.state.showPrivateKey,
                        })
                      }
                      edge='end'
                    >
                      {this.state.showPrivateKey ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                    <IconButton
                      aria-label='toggle password visibility'
                      onClick={() =>
                        this.copyToClipboard(privateKey, 'Private Key')
                      }
                      // onMouseDown={handleMouseDownPassword}
                      edge='end'
                    >
                      <FileCopyIcon />
                    </IconButton>
                  </InputAdornment>
                }
                label='PRIVATE KEY'
              />
            </FormControl>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <FormControl
              variant='outlined'
              style={{ flex: '1', margin: '20px' }}
            >
              <InputLabel htmlFor='endpoint url'>
                FlowCharts AI's base endpoint
              </InputLabel>
              <OutlinedInput
                id='endpoint url'
                type={'text'}
                value={apiUrl}
                inputProps={{
                  readOnly: true,
                }}
                endAdornment={
                  <InputAdornment position='end'>
                    <IconButton
                      aria-label='copy endpoint url'
                      onClick={() => this.copyToClipboard(apiUrl, 'API URL')}
                      edge='end'
                    >
                      <FileCopyIcon />
                    </IconButton>
                  </InputAdornment>
                }
                label="FlowCharts AI's base endpoint"
              />
            </FormControl>
          </div>
        </div>
      </>
    );
  }

  renderSnackbar() {
    return (
      <Snackbar
        open={this.snackMessage != ''}
        autoHideDuration={5000}
        onClose={() => (this.snackMessage = '')}
      >
        <Alert
          elevation={6}
          variant='filled'
          severity='info'
          onClose={() => (this.snackMessage = '')}
        >
          {this.snackMessage}
        </Alert>
      </Snackbar>
    );
  }
  renderTab() {
    return (
      <Paper>
        <div
          style={{
            display: 'flex',
            columnGap: '40px',
            justifyContent: 'space-evently',
            margin: '1rem 0',
            padding: '2rem 2.5rem',
          }}
        >
          <Button
            variant='contained'
            color='primary'
            style={{ flex: '1' }}
            onClick={() =>
              this.setState({ ...this.state, renderType: 'TABLE' })
            }
          >
            <TocIcon style={{ marginRight: '0.6rem' }} />
            Table
          </Button>
          <Button
            variant='contained'
            color='primary'
            style={{ flex: '1' }}
            onClick={() =>
              this.setState({ ...this.state, renderType: 'CHART' })
            }
          >
            <BarChartIcon style={{ marginRight: '0.6rem' }} />
            Chart
          </Button>
          <Button
            variant='contained'
            color='primary'
            style={{ flex: '1' }}
            onClick={() => this.setState({ ...this.state, renderType: 'API' })}
          >
            <CodeIcon style={{ marginRight: '0.6rem' }} />
            API
          </Button>
        </div>
      </Paper>
    );
  }

  renderInstruction() {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          margin: '1rem 0',
          padding: '1rem 2rem',
        }}
      >
        <h1
          style={{
            fontSize: '22px',
            margin: '10px 0',
            lineHeight: 'inherit',
            fontWeight: 'inherit',
          }}
        >
          API Instruction
        </h1>
        <ul style={{ listStyleType: 'inherit' }}>
          <li style={{ marginBottom: '0.6rem' }}>
            You can use our API to connect your FlowCharts data to feed into
            another software application. To implement the API, copy the base
            endpoint URL. Next, copy your API Key & Private Key, and replace
            where it writes YOUR_API_KEY & YOUR_PRIVATE_KEY in the base endpoint
            with your API Key and Private Key respectively.
          </li>
          <li style={{ marginBottom: '0.6rem' }}>
            For example, you'll start with:
            <u>
              https://us-central1-ai-flowchart.cloudfunctions.net/api/questionnaire?API_KEY=
              <strong>YOUR_API_KEY</strong>&PRIVATE_KEY=
              <strong>YOUR_PRIVATE_KEY</strong>
            </u>
          </li>
          <li style={{ marginBottom: '0.6rem' }}>
            Then if your API Key is: 123456789 and your Private Key is:
            987654321 your base endpoint URL will be:
            <u>
              https://us-central1-ai-flowchart.cloudfunctions.net/api/questionnaire?API_KEY=
              <strong>123456789</strong>&PRIVATE_KEY=<strong>987654321</strong>
            </u>
          </li>
          <li style={{ marginBottom: '0.6rem' }}>
            If you put this URL in a browser (visit the URL), you'll see the
            output that you can use with your system.
          </li>
        </ul>
      </div>
    );
  }

  renderCharts() {
    const customData = this.currentNarratives.map((n) => n.responses);
    return (
      <div>
        <Paper>{this.renderQuestionsHelp()}</Paper>
        <Paper>
          <CustomHorizontalChart narrativesData={customData} />
        </Paper>
        {/* <Paper>
          <CustomPieChart narrativesData={customData} />
        </Paper> */}
      </div>
    );
  }

  render() {
    return (
      <React.Fragment>
        <div
          style={{
            width: '100%',
            height: 'env(safe-area-inset-top)',
            backgroundColor: '#3f51b5',
          }}
        />
        {this.renderNavBar()}
        <Container
          component='main'
          style={{ marginTop: '36px', paddingBottom: '2rem' }}
          maxWidth='md'
        >
          {this.renderTab()}
          {this.state.renderType === 'TABLE' && (
            <Paper>{this.renderTable()}</Paper>
          )}
          {this.state.renderType === 'API' && (
            <>
              <Paper>{this.renderAPI()}</Paper>
              <Paper>{this.renderInstruction()}</Paper>
            </>
          )}
          {this.state.renderType === 'CHART' && (
            <>
              {this.currentNarratives.length == 0
                ? this.renderEmpty('charts')
                : this.renderCharts()}
            </>
          )}
          {this.renderSnackbar()}
        </Container>

        <Dialog
          open={this._deleteDialog}
          onClose={this.handleDeleteDialogClose}
          aria-labelledby='alert-dialog-title'
          aria-describedby='alert-dialog-description'
        >
          <DialogTitle id='alert-dialog-title'>{`Delete ${this._narrativeToDelete?.userName}'s narrative ?`}</DialogTitle>
          <DialogContent>
            <DialogContentText id='alert-dialog-description'>
              Are you sure you want to delete the narrative of{' '}
              {this._narrativeToDelete?.userName} ?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => this.handleDeleteDialogClose(false)}
              color='primary'
            >
              NO
            </Button>
            <Button
              onClick={() => this.handleDeleteDialogClose(true)}
              color='primary'
              autoFocus
            >
              YES
            </Button>
          </DialogActions>
        </Dialog>
        {this.props.account.IsPlanExpired()[2].count > 0 ? (
          <Snackbar open={this.props.account.IsPlanExpired()[2].limit}>
            <div
              style={{
                backgroundColor: '#2196f3',
                color: 'white',
                padding: '20px',
                borderRadius: '5px',
              }}
            >
              {`${
                this.props.account.IsPlanExpired()[2].count
              } more narratives available!`}
              <Button
                variant='outlined'
                onClick={this.handleUpgradeClick}
                style={{ color: '#FFF', borderColor: '#FFF', margin: '10px' }}
              >
                Upgrade
              </Button>{' '}
              to view them all!
            </div>
          </Snackbar>
        ) : null}
      </React.Fragment>
    );
  }
}
